import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { PageFlip } from "page-flip";
import "./AURFullBookReader.css";
import { useLanguage } from "../shared/LanguageContext";
import { websiteCopy } from "../shared/websiteCopy";
import {
  createProtectedBookStore,
} from "../shared/protectedBookStore";

const PAGE_WIDTH = 550;
const PAGE_HEIGHT = 733;
const FIRST_PAGE = 1;
const DEFAULT_LAST_PAGE = 126;
const MAX_PAGE_WIDTH = 1100;
const MAX_PAGE_HEIGHT = Math.ceil(
  MAX_PAGE_WIDTH * (PAGE_HEIGHT / PAGE_WIDTH)
);
const MAX_DPR = 2;

const SINGLE_PAGE_LAYOUT_MIN_WIDTH = 280;

const MIN_ZOOM_SCALE = 1;
const MAX_ZOOM_SCALE = 3;
const SWIPE_THRESHOLD = 12;
const ACTIVE_PAGE_WINDOW_RADIUS = 2;

const AURFullBookReader = forwardRef(function AURFullBookReader(
  {
    currentPage = FIRST_PAGE,
    onPageChange,
  },
  ref
) {
  const { language, t } = useLanguage();
  const copy = websiteCopy[language].reader;
  const bookRef = useRef(null);
  const pageFlipRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rafRef = useRef(null);
  const zoomRafRef = useRef(null);
  const readyRef = useRef(false);
  const totalPagesRef = useRef(DEFAULT_LAST_PAGE);
  const bookStoreRef = useRef(null);
  const unsubscribeBookStoreRef = useRef(null);
  const activeWindowRef = useRef({
    start: FIRST_PAGE,
    end: FIRST_PAGE,
  });
  const displayPageRef = useRef(FIRST_PAGE);
  const windowSyncRafRef = useRef(null);
  const suppressPageFlipEventRef = useRef(false);
  const navigationRequestRef = useRef(0);

  const zoomRef = useRef({
    scale: MIN_ZOOM_SCALE,
    panX: 0,
    panY: 0,
  });

  const gestureRef = useRef({
    mode: "idle",
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    initialDistance: 0,
    initialScale: MIN_ZOOM_SCALE,
    initialPanX: 0,
    initialPanY: 0,
    initialMidpointX: 0,
    initialMidpointY: 0,
  });

  const [displayPage, setDisplayPage] = useState(currentPage);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [totalPages, setTotalPages] = useState(DEFAULT_LAST_PAGE);
  const [bookReadyRevision, setBookReadyRevision] = useState(0);

  const getBookCanvas = () => {
    const root = bookRef.current;
    if (!root) return null;
    return root.querySelector("canvas.stf__canvas");
  };

  const getZoomBounds = (scale) => {
    const root = bookRef.current;
    const canvas = getBookCanvas();

    if (!root || !canvas) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const rootRect = root.getBoundingClientRect();
    const baseWidth = canvas.offsetWidth || canvas.clientWidth;
    const baseHeight = canvas.offsetHeight || canvas.clientHeight;

    if (!baseWidth || !baseHeight) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    }

    const scaledWidth = baseWidth * scale;
    const scaledHeight = baseHeight * scale;

    const centeredX =
      scaledWidth < rootRect.width
        ? (rootRect.width - scaledWidth) / 2
        : 0;
    const centeredY =
      scaledHeight < rootRect.height
        ? (rootRect.height - scaledHeight) / 2
        : 0;

    return {
      minX:
        scaledWidth > rootRect.width
          ? rootRect.width - scaledWidth
          : centeredX,
      maxX:
        scaledWidth > rootRect.width
          ? 0
          : centeredX,
      minY:
        scaledHeight > rootRect.height
          ? rootRect.height - scaledHeight
          : centeredY,
      maxY:
        scaledHeight > rootRect.height
          ? 0
          : centeredY,
    };
  };

  const clampZoomPan = (scale, panX, panY) => {
    if (scale <= MIN_ZOOM_SCALE) {
      return {
        scale: MIN_ZOOM_SCALE,
        panX: 0,
        panY: 0,
      };
    }

    const bounds = getZoomBounds(scale);

    return {
      scale,
      panX: Math.min(bounds.maxX, Math.max(bounds.minX, panX)),
      panY: Math.min(bounds.maxY, Math.max(bounds.minY, panY)),
    };
  };

  const applyZoomTransform = () => {
    const canvas = getBookCanvas();
    if (!canvas) return;

    const state = clampZoomPan(
      zoomRef.current.scale,
      zoomRef.current.panX,
      zoomRef.current.panY
    );

    zoomRef.current = state;

    canvas.style.transformOrigin = "0 0";
    canvas.style.transform =
      `translate3d(${state.panX}px, ${state.panY}px, 0) ` +
      `scale(${state.scale})`;
    canvas.style.willChange =
      state.scale > MIN_ZOOM_SCALE ? "transform" : "auto";
  };

  const scheduleZoomTransform = () => {
    if (typeof window === "undefined") return;

    if (zoomRafRef.current !== null) {
      window.cancelAnimationFrame(zoomRafRef.current);
    }

    zoomRafRef.current = window.requestAnimationFrame(() => {
      applyZoomTransform();
      zoomRafRef.current = null;
    });
  };

  const resetZoom = () => {
    zoomRef.current = {
      scale: MIN_ZOOM_SCALE,
      panX: 0,
      panY: 0,
    };

    scheduleZoomTransform();
  };

  const getTouchDistance = (touchA, touchB) => {
    const dx = touchB.clientX - touchA.clientX;
    const dy = touchB.clientY - touchA.clientY;
    return Math.hypot(dx, dy);
  };

  const getTouchMidpoint = (touchA, touchB) => ({
    x: (touchA.clientX + touchB.clientX) / 2,
    y: (touchA.clientY + touchB.clientY) / 2,
  });

  const beginPinch = (touchA, touchB) => {
    const root = bookRef.current;
    if (!root) return;

    const midpoint = getTouchMidpoint(touchA, touchB);
    const rect = root.getBoundingClientRect();

    gestureRef.current = {
      mode: "pinch",
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      initialDistance: Math.max(1, getTouchDistance(touchA, touchB)),
      initialScale: zoomRef.current.scale,
      initialPanX: zoomRef.current.panX,
      initialPanY: zoomRef.current.panY,
      initialMidpointX: midpoint.x - rect.left,
      initialMidpointY: midpoint.y - rect.top,
    };
  };

  const updatePinchZoom = (touchA, touchB) => {
    const gesture = gestureRef.current;
    if (!gesture.initialDistance) return;

    const currentDistance = getTouchDistance(touchA, touchB);
    const scale = Math.min(
      MAX_ZOOM_SCALE,
      Math.max(
        MIN_ZOOM_SCALE,
        gesture.initialScale *
          (currentDistance / gesture.initialDistance)
      )
    );

    const root = bookRef.current;
    if (!root) return;

    const rect = root.getBoundingClientRect();
    const midpoint = getTouchMidpoint(touchA, touchB);

    const midpointX = midpoint.x - rect.left;
    const midpointY = midpoint.y - rect.top;

    const scaleRatio =
      scale / (gesture.initialScale || MIN_ZOOM_SCALE);

    const anchoredPanX =
      midpointX -
      gesture.initialMidpointX * scaleRatio +
      gesture.initialPanX * scaleRatio;

    const anchoredPanY =
      midpointY -
      gesture.initialMidpointY * scaleRatio +
      gesture.initialPanY * scaleRatio;

    zoomRef.current = clampZoomPan(
      scale,
      scale === MIN_ZOOM_SCALE ? 0 : anchoredPanX,
      scale === MIN_ZOOM_SCALE ? 0 : anchoredPanY
    );

    scheduleZoomTransform();
  };

  const syncActiveWindow = async (targetPage) => {
    if (
      !readyRef.current ||
      !pageFlipRef.current ||
      !bookStoreRef.current
    ) {
      return false;
    }

    const store = bookStoreRef.current;
    const total = totalPagesRef.current;

    if (
      !Number.isInteger(targetPage) ||
      targetPage < FIRST_PAGE ||
      targetPage > total
    ) {
      return false;
    }

    store.prioritize(targetPage);

    if (!store.isReady(targetPage)) {
      return false;
    }

    let start = targetPage;
    let end = targetPage;

    for (
      let offset = 1;
      offset <= ACTIVE_PAGE_WINDOW_RADIUS;
      offset += 1
    ) {
      const previousPage = targetPage - offset;

      if (
        previousPage >= FIRST_PAGE &&
        store.isReady(previousPage)
      ) {
        start = previousPage;
      } else {
        break;
      }
    }

    for (
      let offset = 1;
      offset <= ACTIVE_PAGE_WINDOW_RADIUS;
      offset += 1
    ) {
      const nextPage = targetPage + offset;

      if (
        nextPage <= total &&
        store.isReady(nextPage)
      ) {
        end = nextPage;
      } else {
        break;
      }
    }

    const urls = [];

    for (let page = start; page <= end; page += 1) {
      const url = await store.getPageUrl(page);

      if (!url) {
        return false;
      }

      urls.push(url);
    }

    if (!pageFlipRef.current || !bookStoreRef.current) {
      return false;
    }

    const currentWindow = activeWindowRef.current;

    if (
      currentWindow.start === start &&
      currentWindow.end === end
    ) {
      return true;
    }

    suppressPageFlipEventRef.current = true;

    try {
      pageFlipRef.current.updateFromImages(urls);

      activeWindowRef.current = {
        start,
        end,
      };

      const relativePage = targetPage - start;

      pageFlipRef.current.turnToPage(relativePage);

      displayPageRef.current = targetPage;
      setDisplayPage(targetPage);

      resetZoom();
      scheduleHighDpiCanvas();

      for (
        let page = currentWindow.start;
        page <= currentWindow.end;
        page += 1
      ) {
        if (page < start || page > end) {
          store.releasePageUrl(page);
        }
      }
    } finally {
      globalThis.requestAnimationFrame(() => {
        suppressPageFlipEventRef.current = false;
      });
    }

    return true;
  };

  const scheduleActiveWindowSync = (targetPage) => {
    if (windowSyncRafRef.current) {
      globalThis.cancelAnimationFrame(windowSyncRafRef.current);
    }

    windowSyncRafRef.current = globalThis.requestAnimationFrame(() => {
      windowSyncRafRef.current = null;

      void syncActiveWindow(targetPage);
    });
  };

  const isBookPageReady = (page) => {
    const store = bookStoreRef.current;

    if (!store) {
      return false;
    }

    return store.isReady(page);
  };

  const flipNext = async () => {
    if (
      !readyRef.current ||
      !pageFlipRef.current ||
      !bookStoreRef.current
    ) {
      return;
    }

    const currentPage = displayPageRef.current;
    const nextPage = currentPage + 1;

    const navigationRequest =
      navigationRequestRef.current + 1;

    navigationRequestRef.current =
      navigationRequest;

    if (nextPage > totalPagesRef.current) {
      return;
    }

    const store = bookStoreRef.current;
    store.prioritize(nextPage);

    if (!store.isReady(nextPage)) {
      return;
    }

    let currentWindow = activeWindowRef.current;
    let relativeNext = nextPage - currentWindow.start;

    if (
      relativeNext < 0 ||
      relativeNext >= pageFlipRef.current.getPageCount()
    ) {
      const synced = await syncActiveWindow(nextPage);

      if (!synced) {
        return;
      }

      if (
        navigationRequestRef.current !==
        navigationRequest
      ) {
        return;
      }

      currentWindow = activeWindowRef.current;
      relativeNext = nextPage - currentWindow.start;
    }

    if (
      !pageFlipRef.current ||
      !bookStoreRef.current ||
      !bookStoreRef.current.isReady(nextPage)
    ) {
      return;
    }

    if (
      relativeNext < 0 ||
      relativeNext >= pageFlipRef.current.getPageCount()
    ) {
      return;
    }

    resetZoom();
    pageFlipRef.current.flipNext("bottom");
  };

  const flipPrevious = async () => {
    if (
      !readyRef.current ||
      !pageFlipRef.current ||
      !bookStoreRef.current
    ) {
      return;
    }

    const currentPage = displayPageRef.current;
    const previousPage = currentPage - 1;

    const navigationRequest =
      navigationRequestRef.current + 1;

    navigationRequestRef.current =
      navigationRequest;

    if (previousPage < FIRST_PAGE) {
      return;
    }

    const store = bookStoreRef.current;
    store.prioritize(previousPage);

    if (!store.isReady(previousPage)) {
      return;
    }

    let currentWindow = activeWindowRef.current;
    let relativePrevious = previousPage - currentWindow.start;

    if (
      relativePrevious < 0 ||
      relativePrevious >= pageFlipRef.current.getPageCount()
    ) {
      const synced = await syncActiveWindow(previousPage);

      if (!synced) {
        return;
      }

      if (
        navigationRequestRef.current !==
        navigationRequest
      ) {
        return;
      }

      currentWindow = activeWindowRef.current;
      relativePrevious = previousPage - currentWindow.start;
    }

    if (
      !pageFlipRef.current ||
      !bookStoreRef.current ||
      !bookStoreRef.current.isReady(previousPage)
    ) {
      return;
    }

    if (
      relativePrevious < 0 ||
      relativePrevious >= pageFlipRef.current.getPageCount()
    ) {
      return;
    }

    resetZoom();

    const pageFlip = pageFlipRef.current;

    if (!pageFlip) {
      return;
    }

    if (pageFlip.getCurrentPageIndex() <= 0) {
      return;
    }

    const bounds = pageFlip.getBoundsRect();
    const flipController = pageFlip.getFlipController();

    flipController.flip({
      x: bounds.left + 10,
      y: 1,
    });
  };

  const handleTouchStart = (event) => {
    if (!event.touches || event.touches.length === 0) return;

    if (event.touches.length >= 2) {
      beginPinch(event.touches[0], event.touches[1]);
      event.preventDefault();
      return;
    }

    const touch = event.touches[0];

    gestureRef.current = {
      mode:
        zoomRef.current.scale > MIN_ZOOM_SCALE
          ? "pan"
          : "swipe",
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      initialDistance: 0,
      initialScale: zoomRef.current.scale,
      initialPanX: zoomRef.current.panX,
      initialPanY: zoomRef.current.panY,
      initialMidpointX: 0,
      initialMidpointY: 0,
    };
  };

  const handleTouchMove = (event) => {
    if (!event.touches || event.touches.length === 0) return;

    if (event.touches.length >= 2) {
      if (gestureRef.current.mode !== "pinch") {
        beginPinch(event.touches[0], event.touches[1]);
      }

      updatePinchZoom(event.touches[0], event.touches[1]);
      event.preventDefault();
      return;
    }

    if (gestureRef.current.mode !== "pan") return;

    const touch = event.touches[0];
    const gesture = gestureRef.current;

    const dx = touch.clientX - gesture.lastX;
    const dy = touch.clientY - gesture.lastY;

    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;

    zoomRef.current = clampZoomPan(
      zoomRef.current.scale,
      zoomRef.current.panX + dx,
      zoomRef.current.panY + dy
    );

    scheduleZoomTransform();
    event.preventDefault();
  };

  const handleTouchEnd = (event) => {
    const gesture = gestureRef.current;

    if (event.touches && event.touches.length >= 2) return;

    if (event.touches && event.touches.length === 1) {
      const touch = event.touches[0];

      if (zoomRef.current.scale > MIN_ZOOM_SCALE) {
        gestureRef.current = {
          ...gesture,
          mode: "pan",
          lastX: touch.clientX,
          lastY: touch.clientY,
        };
      } else {
        gestureRef.current = {
          ...gesture,
          mode: "swipe",
          startX: touch.clientX,
          startY: touch.clientY,
          lastX: touch.clientX,
          lastY: touch.clientY,
        };
      }

      return;
    }

    gestureRef.current = {
      ...gesture,
      mode: "idle",
    };

    if (gesture.mode === "pinch") {
      if (zoomRef.current.scale <= MIN_ZOOM_SCALE) {
        resetZoom();
      }
      return;
    }

    if (gesture.mode === "pan") return;
    if (gesture.mode !== "swipe") return;

    if (!event.changedTouches || !event.changedTouches.length) {
      return;
    }

    const touch = event.changedTouches[0];
    const dx = touch.clientX - gesture.startX;
    const dy = touch.clientY - gesture.startY;

    if (
      Math.abs(dx) >= SWIPE_THRESHOLD &&
      Math.abs(dx) > Math.abs(dy)
    ) {
      if (dx < 0) {
        flipNext();
      } else {
        flipPrevious();
      }

      return;
    }

    if (
      Math.abs(dx) >= SWIPE_THRESHOLD ||
      Math.abs(dy) >= SWIPE_THRESHOLD
    ) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width) return;

    const midpoint = rect.left + rect.width / 2;

    if (touch.clientX >= midpoint) {
      flipNext();
    } else {
      flipPrevious();
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      next: flipNext,
      previous: flipPrevious,
      isReady() {
        return readyRef.current;
      },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      if (cancelled || !bookRef.current) return;

      setLoadError("");
      setIsReady(false);
      readyRef.current = false;

      const manifestResponse = await fetch(
        "/api/fulfillment/book/manifest",
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        }
      );

      if (
        manifestResponse.status === 401 ||
        manifestResponse.status === 403
      ) {
        throw new Error(
          "Protected digital-book access is not authorized or has expired."
        );
      }

      if (!manifestResponse.ok) {
        throw new Error(
          "The protected digital book could not be opened."
        );
      }

      const manifest = await manifestResponse.json();
      const manifestPageCount = Number(manifest.page_count);

      if (
        !Number.isInteger(manifestPageCount) ||
        manifestPageCount < FIRST_PAGE
      ) {
        throw new Error(
          "The protected digital book could not be opened."
        );
      }

      totalPagesRef.current = manifestPageCount;
      setTotalPages(manifestPageCount);

      const protectedBookStore = await createProtectedBookStore({
        language,
        manifest,
        pageUrl: (page) =>
          manifest?.page_urls?.[page - FIRST_PAGE] ||
          `/api/fulfillment/book/page/${page}.png`,
      });

      if (cancelled) {
        protectedBookStore.dispose();
        return;
      }

      bookStoreRef.current = protectedBookStore;

      displayPageRef.current = FIRST_PAGE;

      activeWindowRef.current = {
        start: FIRST_PAGE,
        end: FIRST_PAGE,
      };

      const firstPageUrl = await protectedBookStore.waitForPage(
        FIRST_PAGE
      );

      if (cancelled) {
        protectedBookStore.dispose();

        if (bookStoreRef.current === protectedBookStore) {
          bookStoreRef.current = null;
        }

        return;
      }

      const pageFlip = new PageFlip(bookRef.current, {
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        size: "stretch",
        minWidth: SINGLE_PAGE_LAYOUT_MIN_WIDTH,
        maxWidth: MAX_PAGE_WIDTH,
        minHeight: 420,
        maxHeight: MAX_PAGE_HEIGHT,
        drawShadow: true,
        flippingTime: 650,
        usePortrait: true,
        autoSize: true,
        maxShadowOpacity: 0.35,
        showCover: false,
        mobileScrollSupport: false,
        useMouseEvents: false,
        disableFlipByClick: true,
      });

      if (cancelled) {
        pageFlip.destroy();
        return;
      }

      pageFlipRef.current = pageFlip;

      pageFlip.on("flip", (event) => {
        if (suppressPageFlipEventRef.current) {
          return;
        }

        const relativePage = Number(event.data);
        const absolutePage =
          activeWindowRef.current.start + relativePage;

        if (
          !Number.isInteger(absolutePage) ||
          absolutePage < FIRST_PAGE ||
          absolutePage > totalPagesRef.current
        ) {
          return;
        }

        displayPageRef.current = absolutePage;

        setDisplayPage(absolutePage);

        resetZoom();

        onPageChange?.(absolutePage);

        protectedBookStore.prioritize(absolutePage);

        scheduleHighDpiCanvas();

        const activeWindow = activeWindowRef.current;

        if (
          absolutePage < activeWindow.start ||
          absolutePage > activeWindow.end
        ) {
          scheduleActiveWindowSync(absolutePage);
        }
      });

      pageFlip.on("init", () => {
        if (cancelled) return;

        readyRef.current = true;
        setIsReady(true);
        setLoadError("");
        displayPageRef.current = FIRST_PAGE;
        setDisplayPage(FIRST_PAGE);
        resetZoom();
        scheduleHighDpiCanvas();
        scheduleActiveWindowSync(FIRST_PAGE);
      });

      pageFlip.loadFromImages([firstPageUrl]);

      const unsubscribeBookStore = protectedBookStore.subscribe(() => {
        if (cancelled) {
          return;
        }

        setBookReadyRevision((revision) => revision + 1);
      });

      unsubscribeBookStoreRef.current = unsubscribeBookStore;

      if (typeof ResizeObserver !== "undefined") {
        resizeObserverRef.current = new ResizeObserver(() => {
          scheduleHighDpiCanvas();
        });

        resizeObserverRef.current.observe(bookRef.current);
      }
    };

    const handleInitializationError = (error) => {
      if (cancelled) return;

      readyRef.current = false;
      setIsReady(false);
      setLoadError(
        error instanceof Error
          ? error.message
          : "The protected digital book could not be opened."
      );

      if (pageFlipRef.current) {
        pageFlipRef.current.destroy();
        pageFlipRef.current = null;
      }

    };

    const initRaf = window.requestAnimationFrame(() => {
      void initialize().catch(handleInitializationError);
    });

    return () => {
      cancelled = true;
      readyRef.current = false;
      setIsReady(false);

      window.cancelAnimationFrame(initRaf);

      if (windowSyncRafRef.current) {
        globalThis.cancelAnimationFrame(windowSyncRafRef.current);
        windowSyncRafRef.current = null;
      }

      if (unsubscribeBookStoreRef.current) {
        unsubscribeBookStoreRef.current();
        unsubscribeBookStoreRef.current = null;
      }

      if (bookStoreRef.current) {
        bookStoreRef.current.dispose();
        bookStoreRef.current = null;
      }

      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      if (zoomRafRef.current !== null) {
        window.cancelAnimationFrame(zoomRafRef.current);
        zoomRafRef.current = null;
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      if (pageFlipRef.current) {
        pageFlipRef.current.destroy();
        pageFlipRef.current = null;
      }

    };
  }, [onPageChange]);

  useEffect(() => {
    const targetPage = Math.min(
      Math.max(
        Number(currentPage) || FIRST_PAGE,
        FIRST_PAGE
      ),
      totalPagesRef.current
    );

    displayPageRef.current = targetPage;

    const store = bookStoreRef.current;

    if (!store) {
      return;
    }

    store.prioritize(targetPage);

    if (!store.isReady(targetPage)) {
      return;
    }

    void (async () => {
      const synced = await syncActiveWindow(targetPage);

      if (!synced || !pageFlipRef.current) {
        return;
      }

      const activeWindow = activeWindowRef.current;
      const relativePage = targetPage - activeWindow.start;

      if (
        relativePage < 0 ||
        relativePage >= pageFlipRef.current.getPageCount()
      ) {
        return;
      }

      suppressPageFlipEventRef.current = true;

      try {
        pageFlipRef.current.turnToPage(relativePage);

        setDisplayPage(targetPage);
      } finally {
        globalThis.requestAnimationFrame(() => {
          suppressPageFlipEventRef.current = false;
        });
      }
    })();
  }, [currentPage, totalPages]);

  const applyHighDpiCanvas = () => {
    const root = bookRef.current;
    if (!root) return;

    const canvas = root.querySelector("canvas.stf__canvas");
    if (!canvas) return;

    const cssWidth =
      canvas.offsetWidth || canvas.clientWidth;
    const cssHeight =
      canvas.offsetHeight || canvas.clientHeight;

    if (!cssWidth || !cssHeight) return;

    const dpr = Math.min(
      MAX_DPR,
      Math.max(
        1,
        Number(
          typeof window !== "undefined" &&
          window.devicePixelRatio
            ? window.devicePixelRatio
            : 1
        )
      )
    );

    const roundedCssWidth = Math.round(cssWidth);
    const roundedCssHeight = Math.round(cssHeight);

    const targetWidth = Math.max(
      1,
      Math.round(roundedCssWidth * dpr)
    );
    const targetHeight = Math.max(
      1,
      Math.round(roundedCssHeight * dpr)
    );

    canvas.style.width = `${roundedCssWidth}px`;
    canvas.style.height = `${roundedCssHeight}px`;

    if (
      canvas.width !== targetWidth ||
      canvas.height !== targetHeight
    ) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }

    const context = canvas.getContext("2d");
    if (!context) return;

    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.imageSmoothingEnabled = true;

    if ("imageSmoothingQuality" in context) {
      context.imageSmoothingQuality = "high";
    }

    scheduleZoomTransform();
  };

  const scheduleHighDpiCanvas = () => {
    if (typeof window === "undefined") return;

    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = window.requestAnimationFrame(() => {
        applyHighDpiCanvas();
        rafRef.current = null;
      });
    });
  };

  void bookReadyRevision;

  if (loadError) {
    return (
      <section
        className="aur-full-book-reader"
        aria-label={copy.aria}
        data-testid="full-book-reader"
      >
        <div
          className="aur-full-book-reader__error"
          role="alert"
          data-testid="full-book-reader-access-error"
        >
          <p>
            {loadError ===
            "Protected digital-book access is not authorized or has expired."
              ? copy.unauthorized
              : language === "es"
                ? "No se pudo abrir el libro digital protegido."
                : loadError}
          </p>
          <p>{copy.reopen}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="aur-full-book-reader"
      aria-label={copy.aria}
      data-testid="full-book-reader"
    >
      <div
        ref={bookRef}
        className="aur-full-book-reader__book"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{
          touchAction: "none",
          overscrollBehavior: "contain",
        }}
        aria-hidden="true"
        data-testid="full-book-reader-page-view"
      />

      <div
        className="aur-full-book-reader__controls"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          zIndex: 1000,
          boxSizing: "border-box",
          paddingBottom:
            "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <button
          type="button"
          onClick={flipPrevious}
          disabled={
            !readyRef.current ||
            displayPage <= FIRST_PAGE ||
            !isBookPageReady(
              displayPage - 1
            )
          }
          aria-label={copy.previousPage}
          data-testid="full-book-reader-previous-button"
        >
          {t("previous")}
        </button>

        <span
          className="aur-full-book-reader__counter"
          aria-live="polite"
          data-testid="full-book-reader-page-counter"
        >
          {t("page")} {displayPage} {t("of")} {totalPages}
        </span>

        <button
          type="button"
          onClick={flipNext}
          disabled={
            !readyRef.current ||
            displayPage >= totalPages ||
            !isBookPageReady(
              displayPage + 1
            )
          }
          aria-label={copy.nextPage}
          data-testid="full-book-reader-next-button"
        >
          {t("next")}
        </button>
      </div>
    </section>
  );
});

AURFullBookReader.displayName = "AURFullBookReader";

export default AURFullBookReader;
